import { setup, assign, assertEvent } from 'xstate';
import type { JSONContent } from '@tiptap/core';
import { omit } from 'lodash';

// Events

export enum EventType {
  CreateDraft = 'CREATE_DRAFT',
  EditDraft = 'EDIT_DRAFT',
  SaveDraft = 'SAVE_DRAFT',
  DiscardDraft = 'DISCARD_DRAFT',
}

type CreateDraftEvent = {
  type: EventType.CreateDraft;
  id?: string; // Optional for new memos
  document: JSONContent;
};

type EditDraftEvent = {
  type: EventType.EditDraft;
  id?: string; // Optional for new memos
  document: JSONContent;
};

type SaveDraftEvent = {
  type: EventType.SaveDraft;
  id?: string; // Optional for new memos
};

type DiscardDraftEvent = {
  type: EventType.DiscardDraft;
  id?: string; // Optional for new memos
};

export type DraftMachineEvent =
  CreateDraftEvent | EditDraftEvent | SaveDraftEvent | DiscardDraftEvent;

// Context

export interface Draft {
  id?: string; // Existing memos have an ID; new memos do not
  document: JSONContent;
}

export interface DraftMachineContext {
  newDraft?: Draft; // Represents a draft for a new memo
  drafts: Record<string, Draft>; // Represents drafts for existing memos
}

// Machine

export const draftMachine = setup({
  types: {
    context: {} as DraftMachineContext,
    events: {} as DraftMachineEvent,
  },
  actions: {
    createDraft: assign(
      ({ context }, params: { id?: string; document: JSONContent }) => {
        if (!params.id) {
          // Create a draft for a new memo
          return {
            newDraft: {
              id: undefined,
              document: params.document,
            },
            drafts: context.drafts,
          };
        }

        // Create a draft for an existing memo
        return {
          newDraft: context.newDraft,
          drafts: {
            ...context.drafts,
            [params.id]: {
              id: params.id,
              document: params.document,
            },
          },
        };
      },
    ),
    editDraft: assign(
      ({ context }, params: { id?: string; document: JSONContent }) => {
        if (!params.id) {
          // Edit the draft for a new memo
          return {
            newDraft: {
              ...context.newDraft,
              document: params.document,
            },
            drafts: context.drafts,
          };
        }

        // Edit the draft for an existing memo
        const draft = context.drafts[params.id];
        return {
          newDraft: context.newDraft,
          drafts: {
            ...context.drafts,
            [params.id]: {
              ...draft,
              document: params.document,
            },
          },
        };
      },
    ),
    saveDraft: assign(({ context }, params: { id?: string }) => {
      if (!params.id && context.newDraft) {
        // Save the draft for a new memo (no-op)
        return {
          newDraft: { ...context.newDraft },
          drafts: context.drafts,
        };
      }

      if (params.id) {
        const draft = context.drafts[params.id];
        if (draft) {
          // Save the draft for an existing memo (no-op)
          return {
            newDraft: context.newDraft,
            drafts: {
              ...context.drafts,
              [params.id]: { ...draft },
            },
          };
        }
      }

      return context;
    }),
    discardDraft: assign(({ context }, params: { id?: string }) => {
      if (!params.id && context.newDraft) {
        // Discard the draft for a new memo
        return {
          newDraft: undefined,
          drafts: context.drafts,
        };
      }

      if (params.id) {
        // Discard the draft for an existing memo
        return {
          newDraft: context.newDraft,
          drafts: omit(context.drafts, params.id),
        };
      }

      return context;
    }),
  },
}).createMachine({
  id: 'draftMachine',
  context: {
    newDraft: undefined,
    drafts: {},
  },
  on: {
    [EventType.CreateDraft]: {
      actions: {
        type: 'createDraft',
        params: ({ event }) => {
          assertEvent(event, EventType.CreateDraft);
          return { id: event.id, document: event.document };
        },
      },
    },
    [EventType.EditDraft]: {
      actions: {
        type: 'editDraft',
        params: ({ event }) => {
          assertEvent(event, EventType.EditDraft);
          return { id: event.id, document: event.document };
        },
      },
    },
    [EventType.SaveDraft]: {
      actions: {
        type: 'saveDraft',
        params: ({ event }) => {
          assertEvent(event, EventType.SaveDraft);
          return { id: event.id };
        },
      },
    },
    [EventType.DiscardDraft]: {
      actions: {
        type: 'discardDraft',
        params: ({ event }) => {
          assertEvent(event, EventType.DiscardDraft);
          return { id: event.id };
        },
      },
    },
  },
});
